
































































import Vue from "vue";
import { Component, Inject } from "vue-property-decorator";
import { RowClickEventData, TableOptions } from "vue-tables-2-premium";
import { getModule } from "vuex-module-decorators";

import FormErrors from "@/lib/components/FormErrors.vue";
import LoadingSpinner from "@/lib/components/LoadingSpinner.vue";

import { Dictionary } from "@/lib/Dictionary.type";
import TableColumnDefinitionInterface from "@/lib/interfaces/table-column-definition.interface";
import TableColumnDefinitionsParserService from "@/lib/services/table-column-definitions-parser.service";
import dataStore from "@/store";
import AuthenticatedUserProvider from "@/modules/account/authenticated-user-provider.service";
import { Resource } from "@/modules/account/resource";
import ErrorConverterService from "@/modules/forms/error-converter.service";
import Factory from "@/modules/factory/factory.model";
import FactoryStore from "@/modules/factory/store";

import FactoryRushCapacityStore from "../store";

interface TableRow {
  id: string;
  isEnabled: boolean;
  enabled: boolean;
  enabledSortOrder: number;
  name: string;
}

@Component({
  metaInfo: {
    title: "Rush Slot Capacity Management",
  },
  components: {
    FormErrors,
    LoadingSpinner,
  },
})
export default class FactoryRushCapacityManagementPage extends Vue {
  @Inject("AuthenticatedUserProvider")
  private fUserProvider!: AuthenticatedUserProvider;

  @Inject("ErrorConverterService")
  private fErrorConverterService!: ErrorConverterService;

  protected fColumns: TableColumnDefinitionInterface[] = [];

  protected fOptions: TableOptions = {
    texts: {
      filterBy: "",
      defaultOption: "",
    },
  };

  private fErrorsList: string[] = [];
  private fDefinitionsParser?: TableColumnDefinitionsParserService;
  private fFactoryDataStore: FactoryStore;
  private fFactoryRushCapacityStore: FactoryRushCapacityStore;
  private fIsLoading = false;
  private fPendingFactoryModeToggle: Dictionary<boolean> = {};
  private fShowInactiveFactories = false;

  get canManageFactoryRushCapacity(): boolean {
    const user = this.fUserProvider.getUser();

    if (!user) {
      return false;
    }

    return (
      user.hasPermissionForResource(Resource.FACTORY_VIEW) &&
      user.hasPermissionForResource(Resource.FACTORY_RUSH_CAPACITY_MANAGE)
    );
  }

  get canViewFactoryRushCapacity(): boolean {
    const user = this.fUserProvider.getUser();

    if (!user) {
      return false;
    }

    return (
      user.hasPermissionForResource(Resource.FACTORY_VIEW) &&
      user.hasPermissionForResource(Resource.FACTORY_RUSH_CAPACITY_VIEW)
    );
  }

  get errorsList(): string[] {
    return this.fErrorsList;
  }

  get isLoading(): boolean {
    return this.fIsLoading;
  }

  get options(): TableOptions {
    return this.fOptions;
  }

  get showInactiveFactoriesCtrlText(): string {
    return this.fShowInactiveFactories ? "Hide inactive" : "Show inactive";
  }

  get tableData(): TableRow[] {
    const factories = this.fShowInactiveFactories
      ? this.fFactoryDataStore.factoriesList
      : this.fFactoryDataStore.activeFactoriesList;

    return factories.map((factory) => this.createTableRow(factory));
  }

  constructor() {
    super();

    this.fFactoryDataStore = getModule(FactoryStore, dataStore);
    this.fFactoryRushCapacityStore = getModule(
      FactoryRushCapacityStore,
      dataStore
    );

    this.fColumns = [
      {
        name: "name",
        header: "Name",
        cssClass: "_name-column -text",
        isFilterable: true,
        isSortable: true,
      },
      {
        name: "enabled",
        header: "Enabled",
        cssClass: "_enabled-column -text",
        isFilterable: true,
        isFilterFromList: true,
        isSortable: true,
        sortKey: "enabledSortOrder",
        filterValues: [
          { id: "true", text: "Yes" },
          { id: "false", text: "No" },
        ],
      },
      {
        name: "actions",
        header: "Actions",
        cssClass: "_actions-column",
        displayConstraint: "min_tabletL",
      },
    ];

    this.fOptions = {
      debounce: 1000,
      listColumns: {},
      filterByColumn: true,
      orderBy: {
        column: "name",
        ascending: true,
      },
      customSorting: {
        enabled: (isAscending: boolean) => {
          return (left: TableRow, right: TableRow) => {
            const result = left.enabledSortOrder - right.enabledSortOrder;

            return isAscending ? result : -result;
          };
        },
      },
      initialPage: 1,
      perPage: 30,
      perPageValues: [30, 50, 100, 150],
    };
  }

  public getColumns(): string[] {
    if (!this.fDefinitionsParser) {
      return [];
    }

    return this.fDefinitionsParser.getColumns();
  }

  public getItemUrl(itemId: string, routeName: string): string {
    const route = this.$router.resolve(this.getItemLocation(itemId, routeName));

    return route.href;
  }

  public onRowClick(data: RowClickEventData<TableRow>): void {
    this.openItem(data.row.id);
  }

  public openItem(id: string): void {
    void this.$router.push(
      this.getItemLocation(id, "FactoryRushCapacityEditPage")
    );
  }

  public toggleShowInactiveFactories(): void {
    this.fShowInactiveFactories = !this.fShowInactiveFactories;

    void this.$router.replace({
      query: {
        ...this.$route.query,
        showInactive: this.fShowInactiveFactories ? "true" : "false",
      },
    });
  }

  public isModeTogglePending(factoryId: string): boolean {
    return this.fPendingFactoryModeToggle[factoryId] === true;
  }

  public async toggleFactoryRushCapacityMode(row: TableRow): Promise<void> {
    if (this.isModeTogglePending(row.id)) {
      return;
    }

    this.fErrorsList = [];
    this.$set(this.fPendingFactoryModeToggle, row.id, true);

    try {
      if (row.isEnabled) {
        await this.fFactoryRushCapacityStore.disableFactoryRushCapacityModeById(
          row.id
        );
      } else {
        await this.fFactoryRushCapacityStore.enableFactoryRushCapacityModeById(
          row.id
        );
      }
    } catch (e) {
      this.fErrorsList = this.fErrorsList.concat(
        this.fErrorConverterService.describeError(e)
      );
    } finally {
      this.$delete(this.fPendingFactoryModeToggle, row.id);
    }
  }

  protected created(): void {
    this.fShowInactiveFactories = this.$route.query.showInactive === "true";

    this.fDefinitionsParser = new TableColumnDefinitionsParserService(
      this.fColumns
    );

    this.fOptions.headings = this.fDefinitionsParser.getHeadings();
    this.fOptions.columnsClasses = this.fDefinitionsParser.getClasses();
    this.fOptions.columnsDisplay = this.fDefinitionsParser.getColumnDisplay();
    this.fOptions.filterable = this.fDefinitionsParser.getFilterable();

    Vue.set(
      this.fOptions,
      "listColumns",
      this.fDefinitionsParser.getListColumns()
    );
    this.fOptions.sortable = this.fDefinitionsParser.getSortable();
  }

  protected mounted(): void {
    void this.loadTableData();
  }

  private async loadTableData(): Promise<void> {
    this.fIsLoading = true;
    this.fErrorsList = [];

    try {
      const factories = await this.fFactoryDataStore.loadFactories();
      const factoryIds = factories.map((factory) => factory.id);

      await this.fFactoryRushCapacityStore.loadFactoryRushCapacityModesByIds({
        ids: factoryIds,
        useCache: false,
      });
    } catch (e) {
      this.fErrorsList = this.fErrorsList.concat(
        this.fErrorConverterService.describeError(e)
      );
    } finally {
      this.fIsLoading = false;
    }
  }

  private createTableRow(factory: Factory): TableRow {
    const isEnabled = this.fFactoryRushCapacityStore.getFactoryRushCapacityModeById(
      factory.id
    );

    return {
      id: factory.id,
      isEnabled,
      enabled: isEnabled,
      enabledSortOrder: isEnabled ? 1 : 0,
      name: factory.name,
    };
  }

  private getItemLocation(
    itemId: string,
    routeName: string
  ): Record<string, unknown> {
    return {
      name: routeName,
      query: {
        id: itemId,
        query: JSON.stringify({}),
        listQuery: JSON.stringify(this.$route.query),
      },
    };
  }
}
